import { ViewType } from '../config/view';
import { createURLState } from '@/shared/lib/hooks/use-url-state';
import { TimeRange } from '@/shared/lib/utils/date-time/time-range';

export const SRM_VIEW_PARAM = 'view';
export const SRM_CI_PARAM = 'ci_id';
export const SRM_TQL_HI_LINK_ID_PARAM = 'tql_hi_link_id';
export const SRM_HI_TYPE_NAME_PARAM = 'hi_type_name';
export const SRM_SOT_ID_PARAM = 'sot_id';
export const SRM_ACTIVE_ONLY_PARAM = 'active_only';
// HI history range selector (JSON-encoded TimeRange).
export const SRM_RANGE_PARAM = 'range';
// CI health header time window (JSON-encoded TimeRange). Persisted in the URL
// so a view remount (e.g. "reload data" swapping the view for a loader) keeps
// the selected range instead of snapping back to the default.
export const SRM_CI_HEALTH_RANGE_PARAM = 'ci_health_range';
// CI health auto-refresh interval in ms; absent means disabled.
export const SRM_CI_HEALTH_INTERVAL_PARAM = 'ci_health_interval';
// Explicit ci-health sub-stage. Drill-down identifiers such as `sot_id` are also
// used inside other stages and therefore cannot identify the active stage.
export const SRM_CI_HEALTH_STAGE_PARAM = 'ci_health_stage';
// HI history filter inputs (persisted by the view's useFilterConfig / url state).
export const SRM_HI_SEARCH_PARAM = 'search';
export const SRM_HI_TYPES_PARAM = 'hi_types';
export const SRM_SEVERITY_PARAM = 'severity';

/**
 * View-scoped params that are cleared whenever the active SRM view changes, so
 * a view's filter/drill-down state doesn't linger in the shared URL. `view`
 * itself and global TQL params (query_name, auto_execute) are intentionally
 * excluded.
 */
export const SRM_VIEW_SCOPED_PARAMS = [
  SRM_CI_PARAM,
  SRM_TQL_HI_LINK_ID_PARAM,
  SRM_HI_TYPE_NAME_PARAM,
  SRM_SOT_ID_PARAM,
  SRM_ACTIVE_ONLY_PARAM,
  SRM_RANGE_PARAM,
  SRM_CI_HEALTH_RANGE_PARAM,
  SRM_CI_HEALTH_INTERVAL_PARAM,
  SRM_CI_HEALTH_STAGE_PARAM,
  SRM_HI_SEARCH_PARAM,
  SRM_HI_TYPES_PARAM,
  SRM_SEVERITY_PARAM,
] as const;

export const useSRMURLState = createURLState<{
  [SRM_VIEW_PARAM]: ViewType;
  [SRM_CI_PARAM]: string;
  [SRM_TQL_HI_LINK_ID_PARAM]: string;
  [SRM_HI_TYPE_NAME_PARAM]: string;
  [SRM_SOT_ID_PARAM]: string;
  [SRM_ACTIVE_ONLY_PARAM]: boolean;
  [SRM_RANGE_PARAM]: TimeRange;
  [SRM_CI_HEALTH_RANGE_PARAM]: TimeRange;
  [SRM_CI_HEALTH_INTERVAL_PARAM]: number;
  [SRM_CI_HEALTH_STAGE_PARAM]: string;
  [SRM_SEVERITY_PARAM]: number;
}>({
  [SRM_VIEW_PARAM]: { default: ViewType.CI_HEALTH },
  // HI history "show active only" toggle. No default: absent means "unset", so a
  // hidden (keep-mounted) view keeps its own state when the param is cleared on
  // view change instead of snapping back to a baked-in value.
  [SRM_ACTIVE_ONLY_PARAM]: { decode: 'boolean' },
  // No default: absent means "unset", so each context falls back to its own
  // initial range instead of snapping to a baked-in value on view change.
  [SRM_RANGE_PARAM]: { decode: 'json' },
  // Same no-default pattern: ci-health falls back to DEFAULT_CI_HEALTH_RANGE /
  // disabled auto-refresh when the params are absent.
  [SRM_CI_HEALTH_RANGE_PARAM]: { decode: 'json' },
  [SRM_CI_HEALTH_INTERVAL_PARAM]: { decode: 'number' },
  [SRM_CI_HEALTH_STAGE_PARAM]: { default: 'registry' },
  [SRM_SEVERITY_PARAM]: { decode: 'number' },
});

export function isViewType(value: unknown): value is ViewType {
  return (Object.values(ViewType) as string[]).includes(value as string);
}

/** Unknown or disallowed URL values fall back to CI health. */
export function resolveSRMView(
  value: unknown,
  allowed?: Iterable<string>,
): ViewType {
  if (!isViewType(value)) return ViewType.CI_HEALTH;
  if (allowed && !new Set(allowed).has(value)) return ViewType.CI_HEALTH;
  return value;
}

/** Replace TQL state while retaining the active SRM view. */
export function getSRMTQLSearchParams(
  previous: URLSearchParams,
  queryName?: string,
) {
  const next = new URLSearchParams();
  const view = previous.get(SRM_VIEW_PARAM);
  if (view) next.set(SRM_VIEW_PARAM, view);
  if (queryName) next.set('query_name', queryName);
  return next;
}